import { z } from 'zod';

import {
  configVersionSchema,
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  score100Schema,
  sortOrderSchema,
  ulidSchema,
} from '../common';
import {
  commentModeSchema,
  keywordMatchTypeSchema,
  platformSchema,
  projectStatusSchema,
  searchCycleStatusSchema,
} from '../enums';

/**
 * `/projects` DTOs — CRUD, keywords and the lifecycle actions.
 *
 * A project is the unit the operator starts and stops. Its lifecycle actions do
 * not mutate state directly: they validate the transition against
 * `PROJECT_TRANSITIONS`, then issue an allowlisted command to the agent that
 * owns the assigned browser profile. The response therefore carries a
 * `commandId`, not a promise that the work already happened.
 */

export const keywordTermSchema = z.string().trim().min(2).max(120);

// ---------------------------------------------------------------------------
// Keywords
// ---------------------------------------------------------------------------

export const keywordSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  term: keywordTermSchema,
  matchType: keywordMatchTypeSchema,
  /** Relative weight inside rule scoring; 1 is neutral. */
  weight: z.number().min(0).max(5),
  isActive: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Keyword = z.infer<typeof keywordSchema>;

export const createKeywordRequestSchema = z.object({
  term: keywordTermSchema,
  matchType: keywordMatchTypeSchema.default('BROAD'),
  weight: z.number().min(0).max(5).default(1),
  isActive: z.boolean().default(true),
});
export type CreateKeywordRequest = z.infer<typeof createKeywordRequestSchema>;

export const updateKeywordRequestSchema = createKeywordRequestSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateKeywordRequest = z.infer<typeof updateKeywordRequestSchema>;

export const bulkCreateKeywordsRequestSchema = z.object({
  keywords: z.array(createKeywordRequestSchema).min(1).max(200),
});
export type BulkCreateKeywordsRequest = z.infer<typeof bulkCreateKeywordsRequestSchema>;

export const keywordListResponseSchema = z.object({
  items: z.array(keywordSchema),
});
export type KeywordListResponse = z.infer<typeof keywordListResponseSchema>;

/**
 * Negative keywords are exclusions, not searches. `matchType` still applies:
 * an EXACT negative keyword must not knock out a post that merely contains the
 * term as a substring of a longer word.
 */
export const negativeKeywordSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  term: keywordTermSchema,
  matchType: keywordMatchTypeSchema,
  createdAt: isoDateTimeSchema,
});
export type NegativeKeyword = z.infer<typeof negativeKeywordSchema>;

export const createNegativeKeywordRequestSchema = z.object({
  term: keywordTermSchema,
  matchType: keywordMatchTypeSchema.default('PHRASE'),
});
export type CreateNegativeKeywordRequest = z.infer<typeof createNegativeKeywordRequestSchema>;

export const updateNegativeKeywordRequestSchema = createNegativeKeywordRequestSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateNegativeKeywordRequest = z.infer<typeof updateNegativeKeywordRequestSchema>;

export const negativeKeywordListResponseSchema = z.object({
  items: z.array(negativeKeywordSchema),
});
export type NegativeKeywordListResponse = z.infer<typeof negativeKeywordListResponseSchema>;

// ---------------------------------------------------------------------------
// Source groups — operator-added Facebook groups whose feed is harvested
// ---------------------------------------------------------------------------

/**
 * A Facebook group whose URL an operator pasted in.
 *
 * The server stores the CANONICAL url and the parsed `externalId`; the client
 * sends whatever the operator copied from the address bar and lets the server
 * normalise it, so `www.facebook.com/groups/123?ref=x` and
 * `facebook.com/groups/123/` become the one row the unique index expects.
 */
export const sourceGroupSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  url: z.string().url(),
  externalId: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SourceGroup = z.infer<typeof sourceGroupSchema>;

/** A Facebook group URL in any of the shapes an address bar produces. */
export const groupUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .describe('URL của group Facebook, ví dụ https://www.facebook.com/groups/123456');

/**
 * The one canonicalisation of a Facebook group URL, shared by the API (which
 * writes the row) and the agent (which navigates to it), so both agree on the
 * one shape the unique index and the navigation allowlist expect.
 *
 * Accepts what an address bar produces — `m.`/`www.`/no-subdomain, a trailing
 * slash, query and hash, an extra path segment — and returns the slug plus the
 * canonical `https://www.facebook.com/groups/<slug>`. Returns null for anything
 * that is not a Facebook group URL, so a paste error is a clean rejection rather
 * than a row pointing at nowhere.
 */
export function parseFacebookGroupUrl(
  input: string,
): { externalId: string; canonicalUrl: string } | null {
  const trimmed = input.trim();
  // Tolerate a bare "facebook.com/groups/123" with no scheme.
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase().replace(/^(m|www|web|mbasic)\./, '');
  if (host !== 'facebook.com') return null;
  // `/groups/<slug>` — slug is digits or a vanity string, first segment only.
  const match = /^\/groups\/([^/?#]+)/.exec(parsed.pathname);
  if (!match) return null;

  /**
   * Decoding lives inside the try for the same reason parsing does: a truncated
   * paste ("…/groups/giam-gia-50%") is a URL as far as `new URL` is concerned,
   * and `decodeURIComponent` throws URIError on it. Outside the try that error
   * escapes the function, the caller's `null` branch never runs, and a paste
   * slip becomes a 500 instead of the "URL group không hợp lệ" it should be.
   */
  let externalId: string;
  try {
    externalId = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (externalId === '' || externalId.length > 200) return null;

  /**
   * The canonical URL is built from the DECODED slug, so a slug carrying an
   * encoded separator ("abc%2Fdef") would produce a URL with an extra path
   * segment — which the agent's navigation allowlist rejects, silently, after
   * the dashboard has already shown the group as active. Refuse it here, where
   * the operator still sees an error.
   */
  if (/[/?#\s]/u.test(externalId)) return null;

  return {
    externalId,
    canonicalUrl: `https://www.facebook.com/groups/${externalId}`,
  };
}

export const createSourceGroupRequestSchema = z.object({
  url: groupUrlSchema,
  /** Optional label; the server defaults it to the slug until a cycle reads the
   *  real group name. */
  name: z.string().trim().max(200).optional(),
});
export type CreateSourceGroupRequest = z.infer<typeof createSourceGroupRequestSchema>;

export const updateSourceGroupRequestSchema = z
  .object({
    name: z.string().trim().max(200),
    isActive: z.boolean(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateSourceGroupRequest = z.infer<typeof updateSourceGroupRequestSchema>;

export const sourceGroupListResponseSchema = z.object({
  items: z.array(sourceGroupSchema),
});
export type SourceGroupListResponse = z.infer<typeof sourceGroupListResponseSchema>;

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export const projectSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  platform: platformSchema,
  status: projectStatusSchema,
  /** Free-text brand/business context handed to the AI scorer as background. */
  brandContext: z.string().nullable(),
  targetRegion: z.string().nullable(),
  locale: z.string().min(2).max(16),
  searchIntervalMinutes: z.number().int().min(5).max(1440),
  maxResultsPerKeyword: z.number().int().min(1).max(500),
  /** Posts below this rule score are stored but never sent to the AI tier. */
  minRuleScoreForAi: score100Schema,
  /** Only harvest posts published within this many days; null = no limit.
   *  Posts whose publish time the parser cannot read are always kept. */
  maxPostAgeDays: z.number().int().min(1).max(365).nullable(),
  /** Ask Facebook for recency-sorted results ("Bài viết mới đây"). */
  preferRecentPosts: z.boolean(),
  /** Assignment is 1:1 — one browser profile runs at most one project. */
  agentId: idSchema.nullable(),
  browserProfileId: idSchema.nullable(),
  lastCycleAt: isoDateTimeSchema.nullable(),
  statusMessage: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Project = z.infer<typeof projectSchema>;

export const createProjectRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2_000).optional(),
  platform: platformSchema.default('FACEBOOK'),
  brandContext: z.string().trim().max(2_000).optional(),
  targetRegion: z.string().trim().max(120).optional(),
  locale: z.string().min(2).max(16).default('vi-VN'),
  searchIntervalMinutes: z.number().int().min(5).max(1440).default(60),
  maxResultsPerKeyword: z.number().int().min(1).max(500).default(30),
  minRuleScoreForAi: score100Schema.default(40),
  maxPostAgeDays: z.number().int().min(1).max(365).optional(),
  preferRecentPosts: z.boolean().default(true),
  /** Keywords may be supplied inline so a project is usable in one round-trip. */
  keywords: z.array(createKeywordRequestSchema).max(200).default([]),
  negativeKeywords: z.array(createNegativeKeywordRequestSchema).max(200).default([]),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

export const updateProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2_000).nullable(),
    brandContext: z.string().trim().max(2_000).nullable(),
    targetRegion: z.string().trim().max(120).nullable(),
    locale: z.string().min(2).max(16),
    searchIntervalMinutes: z.number().int().min(5).max(1440),
    maxResultsPerKeyword: z.number().int().min(1).max(500),
    minRuleScoreForAi: score100Schema,
    maxPostAgeDays: z.number().int().min(1).max(365).nullable(),
    preferRecentPosts: z.boolean(),
    browserProfileId: idSchema.nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

export const projectCountsSchema = z.object({
  keywordCount: z.number().int().min(0),
  negativeKeywordCount: z.number().int().min(0),
  postMatchCount: z.number().int().min(0),
  /** Post matches still in review status NEW. */
  unreviewedPostCount: z.number().int().min(0),
  highScorePostCount: z.number().int().min(0),
  commentJobCount: z.number().int().min(0),
  pendingApprovalCount: z.number().int().min(0),
});
export type ProjectCounts = z.infer<typeof projectCountsSchema>;

export const projectListItemSchema = projectSchema.extend({
  counts: projectCountsSchema,
  agentName: z.string().nullable(),
  browserProfileName: z.string().nullable(),
  /**
   * Outcome of the most recent search cycle.
   *
   * Without these the list could only say WHEN a cycle last ran, so "ran and
   * found nothing" and "never ran" looked identical on screen — the ambiguity
   * that made every diagnosis a database query. `lastCycleStatus` distinguishes a
   * clean empty run from a failed one, and `lastCycleError` carries the reason.
   */
  lastCycleStatus: searchCycleStatusSchema.nullable(),
  lastCyclePostsFound: z.number().int().min(0).nullable(),
  lastCycleError: z.string().nullable(),
});
export type ProjectListItem = z.infer<typeof projectListItemSchema>;

export const projectListQuerySchema = paginationQuerySchema.extend({
  status: z.union([projectStatusSchema, z.array(projectStatusSchema)]).optional(),
  platform: platformSchema.optional(),
  agentId: idSchema.optional(),
  browserProfileId: idSchema.optional(),
  search: z.string().trim().max(120).optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'name', 'lastCycleAt']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type ProjectListQuery = z.infer<typeof projectListQuerySchema>;

export const projectListResponseSchema = paginated(projectListItemSchema);
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

/** Live runtime state, kept apart from the stored project row it describes. */
export const projectRuntimeStatusSchema = z.object({
  status: projectStatusSchema,
  statusMessage: z.string().nullable(),
  since: isoDateTimeSchema,
  activeSearchCycleId: idSchema.nullable(),
  nextCycleAt: isoDateTimeSchema.nullable(),
  /** True when the workspace-wide kill switch is holding this project down. */
  emergencyStopped: z.boolean(),
  /** Transitions the API will currently accept, straight from PROJECT_TRANSITIONS. */
  allowedTransitions: z.array(projectStatusSchema),
});
export type ProjectRuntimeStatus = z.infer<typeof projectRuntimeStatusSchema>;

export const projectDetailResponseSchema = z.object({
  project: projectSchema,
  counts: projectCountsSchema,
  runtime: projectRuntimeStatusSchema,
  keywords: z.array(keywordSchema),
  negativeKeywords: z.array(negativeKeywordSchema),
  /** Operator-added source groups. Empty for platforms without groups. */
  sourceGroups: z.array(sourceGroupSchema).default([]),
  commentMode: commentModeSchema,
});
export type ProjectDetailResponse = z.infer<typeof projectDetailResponseSchema>;

export const deleteProjectResponseSchema = z.object({
  projectId: idSchema,
  deletedAt: isoDateTimeSchema,
});
export type DeleteProjectResponse = z.infer<typeof deleteProjectResponseSchema>;

// ---------------------------------------------------------------------------
// Lifecycle: POST /projects/:id/{start|pause|resume|stop}
// ---------------------------------------------------------------------------

export const PROJECT_LIFECYCLE_ACTIONS = ['start', 'pause', 'resume', 'stop'] as const;
export const projectLifecycleActionSchema = z.enum(PROJECT_LIFECYCLE_ACTIONS);
export type ProjectLifecycleAction = (typeof PROJECT_LIFECYCLE_ACTIONS)[number];

export const startProjectRequestSchema = z.object({
  /** Required when the project has no profile assigned yet; a profile already
   *  running another project is rejected with CONFLICT. */
  browserProfileId: idSchema.optional(),
  runImmediately: z.boolean().default(true),
});
export type StartProjectRequest = z.infer<typeof startProjectRequestSchema>;

export const pauseProjectRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type PauseProjectRequest = z.infer<typeof pauseProjectRequestSchema>;

export const resumeProjectRequestSchema = z.object({
  /** Refuses to resume while the session still needs a human (checkpoint/2FA). */
  ignoreStaleSession: z.boolean().default(false),
});
export type ResumeProjectRequest = z.infer<typeof resumeProjectRequestSchema>;

export const stopProjectRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
  /** Stop even if the agent is offline; the project is marked STOPPED locally
   *  and the agent reconciles on its next `agent.hello`. */
  force: z.boolean().default(false),
});
export type StopProjectRequest = z.infer<typeof stopProjectRequestSchema>;

/**
 * Every lifecycle action answers with the transition the server recorded plus
 * the command it dispatched. `commandId` is null only when no agent round-trip
 * was needed (for example a forced stop of an offline agent).
 */
export const projectLifecycleResponseSchema = z.object({
  projectId: idSchema,
  action: projectLifecycleActionSchema,
  previousStatus: projectStatusSchema,
  status: projectStatusSchema,
  commandId: ulidSchema.nullable(),
  commandExpiresAt: isoDateTimeSchema.nullable(),
  acceptedAt: isoDateTimeSchema,
});
export type ProjectLifecycleResponse = z.infer<typeof projectLifecycleResponseSchema>;

// ---------------------------------------------------------------------------
// Manual actions: POST /projects/:id/run-now and POST /projects/:id/resync
// ---------------------------------------------------------------------------

/**
 * What a manual "Chạy ngay" achieved.
 *
 * There is no `SENT` member on purpose. The endpoint answers 2xx only when the
 * lease was claimed AND the frame reached the agent AND no refusal came back
 * inside the wait window; every other outcome is an HTTP error carrying a code,
 * so a client cannot render a green toast over a command the agent threw away.
 */
export const manualCycleOutcomeSchema = z.enum(['RUNNING', 'COMPLETED']);
export type ManualCycleOutcome = z.infer<typeof manualCycleOutcomeSchema>;

export const runProjectNowResponseSchema = z.object({
  projectId: idSchema,
  commandId: ulidSchema,
  outcome: manualCycleOutcomeSchema,
  /** The cycle lease this request took, i.e. the scheduler's `lastCycleStartedAt`. */
  claimedAt: isoDateTimeSchema,
  /**
   * EARLIEST instant another manual run is accepted — a lower bound, not an
   * appointment. On-demand scanning is the fastest way to get a Facebook account
   * flagged, so the button has a floor; that floor is measured from the last of
   * {this press, the cycle's start, the cycle's END}, and when `outcome` is
   * RUNNING the end has not happened yet. A cycle lasting longer than the
   * cooldown therefore pushes the real reopen past this value. Render it as
   * "sớm nhất": the previous wording promised an exact time and was observed
   * 1m48s early.
   *
   * When `outcome` is COMPLETED the cycle is over, so this is the exact instant.
   */
  nextManualRunAt: isoDateTimeSchema,
  /** How long the server waited for an early refusal before answering. */
  waitedMs: z.number().int().min(0),
});
export type RunProjectNowResponse = z.infer<typeof runProjectNowResponseSchema>;

/**
 * How the agent answered a pushed frame.
 *
 * `PENDING` is the honest answer when the wait window closed first: the frame
 * was delivered, nothing has come back yet. It is NOT success.
 *
 * `SKIPPED` is the other answer that is not success even though the agent said
 * COMPLETED: a configuration whose version is not newer than the one the agent
 * already holds is discarded on arrival, and the agent reports that as
 * "Configuration vN already applied". Nothing changed on the client. Collapsing
 * it into APPLIED is how a resync claimed to have pushed something it had not.
 */
export const agentApplyOutcomeSchema = z.enum([
  'APPLIED',
  'SKIPPED',
  'REJECTED',
  'FAILED',
  'PENDING',
]);
export type AgentApplyOutcome = z.infer<typeof agentApplyOutcomeSchema>;

/**
 * Result of "Đồng bộ lại" — the manual escape hatch for configuration drift.
 *
 * The agent is normally brought up to date on its own (configuration on start,
 * SelectorPack catch-up on reconnect). This exists for when that did not happen,
 * so it reports each half separately: a configuration the agent applied says
 * nothing about whether it also holds the current pack.
 */
export const projectResyncResponseSchema = z.object({
  projectId: idSchema,
  /** The status that was pushed to the agent, i.e. the project's own. */
  status: projectStatusSchema,
  syncedAt: isoDateTimeSchema,
  /** True only when the configuration was applied AND the pack is current. */
  ok: z.boolean(),
  config: z.object({
    commandId: ulidSchema,
    /** `configVersion` after the bump; the agent applies only a NEWER version. */
    version: z.number().int().min(0),
    outcome: agentApplyOutcomeSchema,
    keywordCount: z.number().int().min(0),
    negativeKeywordCount: z.number().int().min(0),
    /**
     * The agent's own words when it refused, or when it says it kept the
     * configuration it already had (`SKIPPED`). Null when it applied ours.
     * Untrusted display text — it comes verbatim from the client machine.
     */
    message: z.string().nullable(),
  }),
  selectorPack: z.object({
    /** False both when nothing needed sending and when sending failed. */
    delivered: z.boolean(),
    /**
     * Machine reason, verbatim from the catch-up path (`already-current`,
     * `adapter-too-old`, …). Left as a free string rather than an enum so the
     * delivery path may grow a reason without this contract lying about it.
     */
    reason: z.string(),
    /** The version the agent was last known to hold. */
    fromVersion: z.number().int().min(0),
    /** The published version, when there is one. */
    toVersion: z.number().int().min(0).nullable(),
  }),
});
export type ProjectResyncResponse = z.infer<typeof projectResyncResponseSchema>;

// ---------------------------------------------------------------------------
// Configuration snapshot pushed to the agent
// ---------------------------------------------------------------------------

/** Keyword shape as the agent sees it — ids only, no scoring internals. */
export const configKeywordSchema = z.object({
  id: idSchema,
  term: keywordTermSchema,
  matchType: keywordMatchTypeSchema,
});
export type ConfigKeyword = z.infer<typeof configKeywordSchema>;

/**
 * What the server pushes to an agent as `agent.configuration_updated`, and what
 * `GET /projects/:id/config-snapshot` returns for inspection.
 *
 * Two invariants hold here:
 *   - It carries no credential of any kind. Facebook cookies and the browser
 *     profile directory live on the client and never travel in either direction;
 *     the profile is referenced by id only.
 *   - Application is idempotent by `version`. The agent compares the three
 *     version numbers from `configVersionSchema` against what it already has and
 *     does nothing when they match, which is what makes redelivery after a
 *     reconnect harmless.
 */
export const projectConfigSnapshotSchema = configVersionSchema.extend({
  projectId: idSchema,
  projectName: z.string(),
  /** Which channel this project harvests. The agent picks its automation module
   *  (URLs, packs, URL grammar) from this — defaulted so a frame from an older
   *  server still parses as a Facebook project. */
  platform: platformSchema.default('FACEBOOK'),
  status: projectStatusSchema,
  locale: z.string().min(2).max(16),
  browserProfileId: idSchema,
  keywords: z.array(configKeywordSchema),
  negativeKeywords: z.array(configKeywordSchema),
  searchIntervalMinutes: z.number().int().min(5).max(1440),
  maxResultsPerKeyword: z.number().int().min(1).max(500),
  /** Age cutoff for harvested posts, in days. `.default(null)` keeps frames
   *  from servers predating the field parseable on newer agents. */
  maxPostAgeDays: z.number().int().min(1).max(365).nullable().default(null),
  /** Recency sorting on the search page. Defaulted for pre-field frames. */
  preferRecentPosts: z.boolean().default(true),
  /**
   * Groups worth searching INSIDE when the main search page runs dry — the
   * server derives them from this very project's harvest history (groups whose
   * posts already matched), so the agent never guesses where to look.
   *
   * The ceiling is the POOL the agent draws from, not the number it visits: the
   * agent shuffles this list per keyword and visits at most
   * `DEEP_SEARCH_MAX_GROUPS_PER_KEYWORD` of it. Raised from 10 to 30 because a
   * pool of 10 over a history of 57 distinct groups meant the same handful was
   * re-drawn every cycle while the rest were never explored — the shuffle can
   * only spread visits across what it is given.
   */
  deepSearchGroups: z
    .array(
      z.object({
        name: z.string().max(200),
        url: z.string().url().max(500),
      }),
    )
    .max(30)
    .default([]),
  /**
   * Groups the operator explicitly added, whose FEED the agent sweeps wholesale
   * and then filters by this project's keywords locally.
   *
   * Distinct from `deepSearchGroups` in both origin and behaviour: those are
   * derived from harvest history and drive a keyword search INSIDE a group as a
   * top-up, while these are chosen by hand and drive a feed sweep as a primary
   * surface — the only way to reach a group the keyword search never surfaced.
   * Capped so one project cannot make a cycle unbounded; the agent still paces
   * itself with the same per-cycle time budget deep search uses.
   */
  sourceGroups: z
    .array(
      z.object({
        name: z.string().max(200),
        url: z.string().url().max(500),
      }),
    )
    .max(50)
    .default([]),
  /** Mirrors the project's comment mode so the agent's local guard can refuse
   *  work the server should never have queued. */
  commentMode: commentModeSchema,
  emergencyStop: z.boolean(),
  issuedAt: isoDateTimeSchema,
});
export type ProjectConfigSnapshot = z.infer<typeof projectConfigSnapshotSchema>;

export const projectConfigSnapshotResponseSchema = z.object({
  snapshot: projectConfigSnapshotSchema,
});
export type ProjectConfigSnapshotResponse = z.infer<typeof projectConfigSnapshotResponseSchema>;
